"use client";

import { useMemo, type JSX } from "react";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type SearchFilterCatalogProps } from "./search-filter.props";
import { useCatalogFilter } from "./useCatalogFilter";
import { CatalogEditingHint } from "./CatalogEditingHint";
import { FacetSelection } from "lib/search/preselectFacets";
import {
  OFFERING_CATALOG_INHERITED_FACETS,
  readPersistedCatalogFacets,
} from "lib/search/catalogFacetStorage";

export const Default = (props: SearchFilterCatalogProps): JSX.Element => {
  console.log(
    "---->[SearchFilterOfferingCatalog:Default] Component",
    props.fields,
  );

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;
  const datasource = fields?.data?.datasource;
  const searchEngine = useSearchEngine(params?.SearchScope);

  // Build the preselect set:
  //   - `taxonomy_applications` / `taxonomy_solutions`: prefer the persisted
  //     snapshot when present; otherwise fall back to the Sitecore datasource
  //     selection. The solution catalog writes the snapshot from its inbound
  //     MegaMenu deep-link hash, so the user's prior facet choices win over
  //     the page-author default.
  //   - `taxonomy_markets` / `taxonomy_industries`: persisted snapshot only;
  //     these have no datasource counterpart on this component.
  // The persisted snapshot is captured once per `datasource.id` lifetime — a
  // later localStorage write during the same datasource is intentionally
  // ignored so the preselect stays stable. Fields not listed here are left
  // untouched so other deep-link state survives.
  const selections: FacetSelection[] = useMemo(() => {
    const persisted = readPersistedCatalogFacets();

    const datasourceApplications = (datasource?.applications?.targetItems || [])
      .map((item) => item.taxonomyLabel?.value ?? "")
      .filter(Boolean);
    const datasourceSolutions = (datasource?.solutions?.targetItems || [])
      .map((item) => item.taxonomyLabel?.value ?? "")
      .filter(Boolean);

    const persistedApplications = persisted.taxonomy_applications;
    const persistedSolutions = persisted.taxonomy_solutions;

    const result: FacetSelection[] = [
      {
        field: "taxonomy_applications",
        values:
          persistedApplications && persistedApplications.length > 0
            ? persistedApplications
            : datasourceApplications,
      },
      {
        field: "taxonomy_solutions",
        values:
          persistedSolutions && persistedSolutions.length > 0
            ? persistedSolutions
            : datasourceSolutions,
      },
    ];

    for (const field of OFFERING_CATALOG_INHERITED_FACETS) {
      const values = persisted[field];
      if (values && values.length > 0) {
        result.push({ field, values });
      }
    }

    return result;
  }, [datasource]);

  useCatalogFilter(searchEngine, datasource, datasource?.id, selections);

  if (!isPageEditing) return <></>;

  return (
    <CatalogEditingHint
      heading="Search filter: offering catalog"
      datasource={datasource}
      stylesParam={params?.styles}
    />
  );
};
